import { useState } from "react";
import { Aviso, Botao, Cartao } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  rotuloDoPeso,
  useAgentesParaCanais,
  useDefinirDistribuicao,
  useDistribuicao,
  useEquipesParaCanais,
  type Distribuicao,
} from "../../nucleo/canais";
import { CaixaDeMarcar, CampoDeEscolha } from "../automacao/apoio";

interface Linha {
  usuario_id: string;
  nome: string;
  peso: number;
  ativo: boolean;
  carga_aberta: number;
}

function EditorDaDistribuicao({
  equipeId,
  distribuicao,
  salvo,
  aoMudarSalvo,
}: {
  equipeId: string;
  distribuicao: Distribuicao;
  /* Vive no pai: salvar recarrega a equipe, o editor é remontado com o dado novo, e o aviso de
     "salva" não pode sumir junto com ele. */
  salvo: boolean;
  aoMudarSalvo: (salvo: boolean) => void;
}) {
  const agentes = useAgentesParaCanais();
  const definir = useDefinirDistribuicao(equipeId);
  const [automatica, setAutomatica] = useState(distribuicao.automatica);
  const [linhas, setLinhas] = useState<Linha[]>(
    distribuicao.membros.map(({ usuario_id, nome, peso, ativo, carga_aberta }) => ({ usuario_id, nome, peso, ativo, carga_aberta })),
  );
  const [novo, setNovo] = useState("");
  const [problema, setProblema] = useState<string | null>(null);

  const disponiveis = (agentes.data ?? []).filter((a) => !linhas.some((l) => l.usuario_id === a.id));

  function mudar(id: string, campos: Partial<Linha>) {
    aoMudarSalvo(false);
    setLinhas((atual) => atual.map((l) => (l.usuario_id === id ? { ...l, ...campos } : l)));
  }

  function salvar() {
    const erro =
      automatica && !linhas.some((l) => l.ativo)
        ? "Para distribuir automaticamente, deixe ao menos um membro disponível."
        : null;
    setProblema(erro);
    if (erro) return;
    definir.mutate(
      {
        automatica,
        membros: linhas.map(({ usuario_id, peso, ativo }) => ({ usuario_id, peso, ativo })),
      },
      { onSuccess: () => aoMudarSalvo(true) },
    );
  }

  return (
    <div data-testid="editor-da-distribuicao">
      <CaixaDeMarcar
        rotulo="Distribuir os chamados desta equipe automaticamente"
        marcada={automatica}
        aoMudar={(valor) => {
          aoMudarSalvo(false);
          setAutomatica(valor);
        }}
        ajuda="O chamado que entra na equipe sem responsável vai para quem tem menos chamados abertos em relação ao peso. Empate: quem recebeu há mais tempo."
      />
      <ul className="lista-de-itens" data-testid="membros-da-equipe">
        {linhas.map((linha) => (
          <li key={linha.usuario_id}>
            <b>{linha.nome}</b> · {linha.carga_aberta} aberto(s)
            <CampoDeEscolha
              rotulo={`Peso de ${linha.nome}`}
              valor={String(linha.peso)}
              aoMudar={(valor) => mudar(linha.usuario_id, { peso: Number(valor) })}
              opcoes={Array.from({ length: 10 }, (_, i) => ({ valor: String(i + 1), rotulo: rotuloDoPeso(i + 1) }))}
            />
            <CaixaDeMarcar
              rotulo={`${linha.nome} está disponível para receber chamados`}
              marcada={linha.ativo}
              aoMudar={(valor) => mudar(linha.usuario_id, { ativo: valor })}
            />
            <Botao
              type="button"
              variante="fantasma"
              tamanho="pequeno"
              aria-label={`Tirar ${linha.nome} da equipe`}
              onClick={() => {
                aoMudarSalvo(false);
                setLinhas((atual) => atual.filter((l) => l.usuario_id !== linha.usuario_id));
              }}
            >
              Tirar da equipe
            </Botao>
          </li>
        ))}
      </ul>
      {linhas.length === 0 ? <p className="nota-rodape">Ninguém na equipe ainda.</p> : null}
      <CampoDeEscolha
        rotulo="Incluir agente na equipe"
        valor={novo}
        aoMudar={(id) => {
          if (!id) return;
          const agente = disponiveis.find((a) => a.id === id);
          if (!agente) return;
          aoMudarSalvo(false);
          setLinhas((atual) => [...atual, { usuario_id: agente.id, nome: agente.nome, peso: 1, ativo: true, carga_aberta: 0 }]);
          setNovo("");
        }}
        vazio="Escolha um agente"
        opcoes={disponiveis.map((a) => ({ valor: a.id, rotulo: a.nome }))}
      />
      {problema ? <Aviso tom="erro" className="mb-3">{problema}</Aviso> : null}
      {definir.error ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(definir.error)}</Aviso> : null}
      {salvo ? <div role="status"><Aviso tom="sucesso" className="mb-3">Distribuição salva.</Aviso></div> : null}
      <Botao type="button" disabled={definir.isPending} onClick={salvar}>
        {definir.isPending ? "Salvando…" : "Salvar a distribuição"}
      </Botao>
    </div>
  );
}

export function PainelDeDistribuicao() {
  const equipes = useEquipesParaCanais();
  const [equipeId, setEquipeId] = useState("");
  const [salvo, setSalvo] = useState(false);
  const distribuicao = useDistribuicao(equipeId);

  return (
    <Cartao titulo="Distribuição automática por equipe">
      <CampoDeEscolha
        rotulo="Equipe"
        valor={equipeId}
        aoMudar={(id) => {
          setSalvo(false);
          setEquipeId(id);
        }}
        vazio="Escolha a equipe"
        opcoes={(equipes.data ?? []).map((e) => ({ valor: e.id, rotulo: e.nome }))}
      />
      {distribuicao.isPending && equipeId ? <p role="status">Carregando a equipe…</p> : null}
      {distribuicao.isError ? <Aviso tom="erro">{mensagemDoConsole(distribuicao.error)}</Aviso> : null}
      {distribuicao.data ? (
        <EditorDaDistribuicao
          key={`${equipeId}-${distribuicao.dataUpdatedAt}`}
          equipeId={equipeId}
          distribuicao={distribuicao.data}
          salvo={salvo}
          aoMudarSalvo={setSalvo}
        />
      ) : null}
    </Cartao>
  );
}
